import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { notificationTemplateApi } from '@/services/notificationApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Mail, Search, Play, CheckSquare } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import type { NotificationTemplate, NotificationChannel, TemplateStatus } from '@/types/notification';

export const NotificationTemplateListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterChannel, setFilterChannel] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterLocale, setFilterLocale] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'notificationTemplate');
  const canEdit = hasPermission('edit', 'notificationTemplate');
  const canDelete = hasPermission('delete', 'notificationTemplate');
  const canUpdateStatus = hasPermission('update', 'notificationTemplate');

  // Build fetch function with filters and sorting
  const fetchTemplatesFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      return notificationTemplateApi.list({
        page: params.page,
        size: params.size,
        sort: sortBy,
        direction: sortDirection,
        channel: filterChannel !== 'all' ? (filterChannel as NotificationChannel) : undefined,
        status: filterStatus !== 'all' ? (filterStatus as TemplateStatus) : undefined,
        locale: filterLocale !== 'all' ? filterLocale : undefined,
        search: searchQuery.trim() || undefined,
      });
    };
  }, [searchQuery, filterChannel, filterStatus, filterLocale, sortBy, sortDirection]);

  // Use paginated list hook
  const {
    data: templates,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<NotificationTemplate>({
    fetchFn: fetchTemplatesFn,
    entityName: 'notification templates',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      await notificationTemplateApi.delete(templateId);
    },
    entityName: 'notification template',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Status change mutation
  const statusChangeMutation = useMutation({
    mutationFn: async ({ templateId, newStatus }: { templateId: string; newStatus: TemplateStatus }) => {
      await notificationTemplateApi.updateStatus(templateId, newStatus);
    },
    entityName: 'notification template',
    operation: 'update',
    successMessage: (_data, variables) => `Template status updated to ${variables.newStatus}`,
    onSuccess: () => {
      refetch();
    },
  });

  // Bulk status update mutation
  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, newStatus }: { ids: string[]; newStatus: TemplateStatus }) => {
      const promises = ids.map((id) => notificationTemplateApi.updateStatus(id, newStatus));
      await Promise.all(promises);
    },
    entityName: 'notification templates',
    operation: 'update',
    successMessage: (_data, variables) => `${variables.ids.length} templates updated`,
    onSuccess: () => {
      setSelectedTemplates(new Set());
      refetch();
    },
  });

  const handleDelete = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    await deleteMutation.mutate(templateId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const handleStatusChange = async (templateId: string, newStatus: TemplateStatus) => {
    await statusChangeMutation.mutateAsync({ templateId, newStatus });
  };

  const handleBulkStatusUpdate = async (newStatus: TemplateStatus) => {
    if (selectedTemplates.size === 0) return;
    if (!window.confirm(`Update ${selectedTemplates.size} templates to ${newStatus}?`)) return;
    await bulkStatusMutation.mutateAsync({ ids: Array.from(selectedTemplates), newStatus });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const toggleSelect = (templateId: string) => {
    const newSelected = new Set(selectedTemplates);
    if (newSelected.has(templateId)) {
      newSelected.delete(templateId);
    } else {
      newSelected.add(templateId);
    }
    setSelectedTemplates(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedTemplates.size === templates.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(templates.map((t) => t.templateId)));
    }
  };

  const getChannelIcon = (channel: NotificationChannel) => {
    switch (channel) {
      case 'EMAIL':
        return '📧';
      case 'SMS':
        return '💬';
      case 'WHATSAPP':
        return '💚';
      case 'PUSH_NOTIFICATION':
        return '🔔';
      case 'IN_APP':
        return '📱';
      default:
        return '📄';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Notification Templates</h1>
          <p className="text-muted-foreground mt-2">Manage notification templates</p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <Button asChild>
              <Link to="/admin/notifications/templates/new">
                <Plus className="mr-2 h-4 w-4" />
                Create Template
              </Link>
            </Button>
          )}
          <Button asChild variant="outline">
            <Link to="/admin/notifications/templates/stats">
              <CheckSquare className="mr-2 h-4 w-4" />
              Statistics
            </Link>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterChannel} onValueChange={setFilterChannel}>
              <SelectTrigger>
                <SelectValue placeholder="All Channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="EMAIL">Email</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
                <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                <SelectItem value="PUSH_NOTIFICATION">Push Notification</SelectItem>
                <SelectItem value="IN_APP">In-App</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterLocale} onValueChange={setFilterLocale}>
              <SelectTrigger>
                <SelectValue placeholder="All Locales" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locales</SelectItem>
                <SelectItem value="en">English (en)</SelectItem>
                <SelectItem value="es">Spanish (es)</SelectItem>
                <SelectItem value="fr">French (fr)</SelectItem>
                <SelectItem value="de">German (de)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedTemplates.size > 0 && canUpdateStatus && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {selectedTemplates.size} template(s) selected
              </div>
              <div className="flex gap-2">
                <Select
                  onValueChange={(value) => handleBulkStatusUpdate(value as TemplateStatus)}
                  disabled={bulkStatusMutation.isLoading}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Bulk Status Update" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Set to Active</SelectItem>
                    <SelectItem value="INACTIVE">Set to Inactive</SelectItem>
                    <SelectItem value="ARCHIVED">Set to Archived</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={() => setSelectedTemplates(new Set())}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Templates ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all notification templates</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading notification templates..." />
          ) : (error || templates.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load notification templates. Please try again or create a new one." : "Get started by creating your first notification template."}
              icon={Mail}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/notifications/templates/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {canUpdateStatus && (
                      <TableHead className="w-12">
                        <input
                          type="checkbox"
                          checked={selectedTemplates.size === templates.length && templates.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </TableHead>
                    )}
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('name')}
                    >
                      Template ID
                      {sortBy === 'name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Locale</TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('status')}
                    >
                      Status
                      {sortBy === 'status' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead
                      className="cursor-pointer"
                      onClick={() => handleSort('updatedAt')}
                    >
                      Updated At
                      {sortBy === 'updatedAt' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.templateId}>
                      {canUpdateStatus && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedTemplates.has(template.templateId)}
                            onChange={() => toggleSelect(template.templateId)}
                            className="rounded border-gray-300"
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{template.templateId}</TableCell>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span>{getChannelIcon(template.channel)}</span>
                          <span>{template.channel}</span>
                        </div>
                      </TableCell>
                      <TableCell>{template.locale}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Select
                            value={template.status}
                            onValueChange={(value) =>
                              handleStatusChange(template.templateId, value as TemplateStatus)
                            }
                            disabled={!canUpdateStatus || statusChangeMutation.isLoading}
                          >
                            <SelectTrigger className="w-[120px] h-7">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="DRAFT">Draft</SelectItem>
                              <SelectItem value="ACTIVE">Active</SelectItem>
                              <SelectItem value="INACTIVE">Inactive</SelectItem>
                              <SelectItem value="ARCHIVED">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </TableCell>
                      <TableCell>
                        {template.updatedAt
                          ? new Date(template.updatedAt).toLocaleDateString()
                          : 'N/A'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/notifications/templates/${template.templateId}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/notifications/templates/${template.templateId}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="Test Template"
                          >
                            <Link to={`/admin/notifications/templates/${template.templateId}?test=true`}>
                              <Play className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(template.templateId)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                    </div>
                    <Select value={pagination.size.toString()} onValueChange={(v) => setSize(parseInt(v))}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationTemplateListPage;






